import { toDateTime } from 'utils/dates';
import { DateTime } from 'luxon';
import invariant from 'tiny-invariant';

export const formatDate = (date: string) =>
  toDateTime(date).toFormat('MM/dd/yyyy');

// Returns whole-number representing the days between date1 and date2
export const calculateDaysBetweenTwoDates = (date1: string, date2: string) => {
  const difference = DateTime.fromISO(date1).diff(DateTime.fromISO(date2), [
    'days',
  ]);
  return Math.floor(Math.abs(difference?.days));
};

export const calculateDifferenceBetweenTwoDates = (
  date1: string,
  date2: string,
) => {
  const difference = DateTime.fromISO(date1).diff(DateTime.fromISO(date2), [
    'days',
    'hours',
  ]);
  return difference;
};

/**
 * Remove time from an ISO date string and format it to a date only string.
 *
 * @param isoDateString - The ISO 8601 formatted date string to format
 * @param format - The desired output format using Luxon's tokens (defaults to 'MM/dd/yyyy')
 *                Common tokens:
 *                - y/yyyy: Year (e.g., 23/2023)
 *                - M/MM/MMM/MMMM: Month (e.g., 7/07/Jul/July)
 *                - d/dd: Day of month (e.g., 3/03)
 *                - E/EEE/EEEE: Day of week (e.g., Tue/Tuesday)
 *                - /,-,.: Common separators
 *
 * Examples:
 * - 'MM/dd/yyyy' -> '07/03/2023'
 * - 'MMM d, yyyy' -> 'Jul 3, 2023'
 * - 'yyyy-MM-dd' -> '2023-07-03'
 *
 * @returns Formatted date string or '-' if input is invalid
 * @see {@link https://moment.github.io/luxon/#/formatting?id=table-of-tokens Luxon formatting tokens}
 */
export const formatIsoToDateOnlyString = (
  isoDateString: string,
  format?: string,
): string => {
  try {
    // Check if string matches ISO 8601 format
    const isoRegex =
      /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])(?:T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[-+](?:[01]\d|2[0-3]):[0-5]\d)?)?$/;
    invariant(isoRegex.test(isoDateString), 'Invalid ISO date format');
    // Parsing the date portion only
    const date = DateTime.fromFormat(isoDateString.split('T')[0], 'yyyy-MM-dd');
    invariant(date.isValid, 'Invalid date components');
    // Format using Luxon
    return date.toFormat(format ?? 'MM/dd/yyyy');
  } catch (error) {
    return '-';
  }
};
